import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
// Use axios' ESM implementation subpath instead of the package root. In CRA's
// webpack config, requiring the package root from this workspace package
// resolves to axios/dist/browser/axios.cjs, and that .cjs file is treated as a
// static asset URL. The unsafe subpath is an official exported escape hatch to
// axios/lib/* and keeps the bundled module as executable JavaScript.
declare const require: (id: string) => any;
const axiosLib: any = require('axios/unsafe/axios.js');
const axios: any =
  typeof axiosLib?.create === 'function'
    ? axiosLib
    : typeof axiosLib?.default?.create === 'function'
      ? axiosLib.default
      : typeof axiosLib?.default?.default?.create === 'function'
        ? axiosLib.default.default
        : axiosLib;

import type { TokenStore } from '../storage/TokenStore.js';
import type {
  GetActiveCompanyId,
  GetStoredUser,
  OnLoginSuccess,
  OnSessionExpired,
  ShouldApplyActiveCompany,
} from './types.js';

/**
 * Factory for the shared axios client used by every service module.
 *
 * The web app and the desktop renderer both call this — the only differences
 * between them are: (a) the baseUrl resolution (web uses
 * REACT_APP_API_URL/window.__RUNTIME_API_URL__, desktop uses the value that
 * the Electron main process injected into the preload-exposed runtime URL)
 * and (b) the TokenStore (web uses sessionStorage/localStorage, desktop will
 * use the OS secure store in M3).
 *
 * Behaviour MUST stay identical to the existing frontend/src/services/api.ts
 * because every existing service depends on it. If you change anything here,
 * exercise the auth flow on web first.
 */

export interface CreateApiClientOptions {
  /** Resolved base URL — caller decides the precedence between env, runtime, etc. */
  baseUrl: string;
  /** Read/write the bearer token from whichever store is appropriate for the host. */
  tokenStore: TokenStore;
  /** Look up the active platform-admin company override (web-specific). */
  getActiveCompanyId?: GetActiveCompanyId;
  /** Decide whether the active-company header should be attached to a given request. */
  shouldApplyActiveCompany?: ShouldApplyActiveCompany;
  /** Read the currently stored user (used by shouldApplyActiveCompany). */
  getStoredUser?: GetStoredUser;
  /** Called when the active company context should be cleared (logout / 401). */
  clearActiveCompanyContext?: () => void;
  /** Notified after a successful /auth/login response is observed. */
  onLoginSuccess?: OnLoginSuccess;
  /** Notified when an authenticated session has unrecoverably expired. */
  onSessionExpired?: OnSessionExpired;
  /** Defaults to 45_000 ms — matches the existing web client. */
  timeoutMs?: number;
}

/**
 * Extended request config that lets the response interceptor mark a request
 * as already having been retried after a token refresh. Mirrors the existing
 * `_isRetry` field on the web client.
 */
type RetriableConfig = InternalAxiosRequestConfig & { _isRetry?: boolean };

export interface ApiClient {
  axios: AxiosInstance;
  baseUrl: string;
}

const LOGIN_GRACE_MS = 15_000;

export function createApiClient(opts: CreateApiClientOptions): ApiClient {
  const {
    baseUrl,
    tokenStore,
    getActiveCompanyId,
    shouldApplyActiveCompany,
    getStoredUser,
    clearActiveCompanyContext,
    onLoginSuccess,
    onSessionExpired,
    timeoutMs = 45_000,
  } = opts;

  const instance = axios.create({
    baseURL: baseUrl,
    timeout: timeoutMs,
    headers: { 'Content-Type': 'application/json' },
  });

  let lastLoginSuccessAt = 0;
  let isRefreshing = false;
  let pendingQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

  function processQueue(error: unknown, token: string | null): void {
    pendingQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
    pendingQueue = [];
  }

  // Request interceptor — attach token, optional active-company header, and
  // let the browser set Content-Type for FormData uploads.
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = await Promise.resolve(tokenStore.getToken());
      if (token) {
        config.headers.set?.('Authorization', `Bearer ${token}`);
        // Defensive set for axios versions where headers is a plain object.
        (config.headers as Record<string, unknown>).Authorization = `Bearer ${token}`;
      }

      if (shouldApplyActiveCompany && getActiveCompanyId) {
        const user = getStoredUser ? getStoredUser() : null;
        if (shouldApplyActiveCompany(user, config.url)) {
          const activeCompanyId = getActiveCompanyId();
          if (activeCompanyId) {
            (config.headers as Record<string, unknown>)['x-active-company-id'] = activeCompanyId;
          }
        }
      }

      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        // Let the browser populate the multipart boundary.
        (config.headers as Record<string, unknown>)['Content-Type'] = undefined as unknown as string;
      }
      return config;
    },
    (error: unknown) => Promise.reject(error),
  );

  // Response interceptor — silent token refresh + forced logout, mirroring
  // the existing web behaviour.
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.config?.url?.includes('/auth/login') && response.status === 200) {
        lastLoginSuccessAt = Date.now();
        if (onLoginSuccess) onLoginSuccess();
      }
      return response;
    },
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        const requestUrl = (error.config?.url as string | undefined) || '';
        const isLoginRequest = requestUrl.includes('/auth/login');
        const isProfileRequest =
          requestUrl.includes('/auth/profile') || requestUrl.includes('/auth/me');
        const isRefreshRequest = requestUrl.includes('/auth/refresh');

        const cfg = error.config as RetriableConfig | undefined;

        if (cfg && !isLoginRequest && !isProfileRequest && !isRefreshRequest && !cfg._isRetry) {
          if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
              pendingQueue.push({ resolve, reject });
            }).then((token) => {
              cfg.headers = cfg.headers || ({} as InternalAxiosRequestConfig['headers']);
              (cfg.headers as Record<string, unknown>).Authorization = `Bearer ${token}`;
              return instance(cfg as AxiosRequestConfig);
            });
          }

          cfg._isRetry = true;
          isRefreshing = true;

          try {
            const refreshUrl = baseUrl.replace(/\/?$/, '') + '/auth/refresh';
            const refreshRes = await axios.post(refreshUrl, {}, { withCredentials: true });
            const newToken: string | undefined = (refreshRes.data as { data?: { token?: string } })
              ?.data?.token;
            if (!newToken) throw new Error('No token in refresh response');

            await Promise.resolve(tokenStore.setToken(newToken));

            processQueue(null, newToken);
            cfg.headers = cfg.headers || ({} as InternalAxiosRequestConfig['headers']);
            (cfg.headers as Record<string, unknown>).Authorization = `Bearer ${newToken}`;
            return instance(cfg as AxiosRequestConfig);
          } catch (refreshError) {
            processQueue(refreshError, null);
            // Fall through to forced logout below.
          } finally {
            isRefreshing = false;
          }
        }

        if (!isLoginRequest && !isProfileRequest) {
          const hadToken = !!(await Promise.resolve(tokenStore.getToken()));
          await Promise.resolve(tokenStore.clearToken());
          if (clearActiveCompanyContext) clearActiveCompanyContext();
          if (
            hadToken &&
            Date.now() - lastLoginSuccessAt > LOGIN_GRACE_MS &&
            onSessionExpired
          ) {
            onSessionExpired();
          }
        }
      }

      // Parse JSON-bearing Blob errors so callers can read .message etc.
      const data = error.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob && data.type === 'application/json') {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          (error.response as { data: unknown }).data = json;
        } catch {
          /* ignore */
        }
      }

      const errData = error.response?.data as { message?: string } | undefined;
      if (errData && !errData.message) {
        errData.message = error.message || 'An error occurred';
      }

      return Promise.reject(error);
    },
  );

  return { axios: instance, baseUrl };
}
